'use client';
import React, { FC, useEffect, useState } from 'react';
import { Errors, HeaderText, Loader, RichText } from '@/components';
import { TypeResponseError } from '@/lib/api/config';
import { TypeSubscriberShopProduct } from '@/lib/api/productCatalog/types';
import { HeaderSize } from '@/components/ui/HeaderText';
import { SiblingsProductCard } from '../Siblings';
import {
  ReactivateSubscriptionModal,
  PlayKitsLimitModal,
  LifetimeMaxModal,
} from '@/components/common/SubscriberShop';
import {
  DEFAULT_PRODUCT_CARDS_COUNT,
  DIVIDER_IMAGES,
  findSequentialKitsOverSixMonths,
} from '@/components/common/SubscriberShop/utils';
import Image from 'next/image';
import { removeFromCart } from '@/lib/cart.browser';
import { captureTypeProductEvent, pushEvent } from '@/utils/dataLayer';
import { TypeSubscriberShopPageData } from '@/pages/[locale]/account/subscriber-shop';
import { TypeSubscriberShopPageTranslations } from '@/utils/translations/types/TypeSubscriberShopPageTranslations';
import generateTranslationsObject from '@/utils/translations/generateTranslationObject';
import {
  TypeModalFields,
  TypeSubscriberShopCollectionsFields,
} from '@/core/generated-types';
import { Document } from '@contentful/rich-text-types';
import { mutate } from 'swr';
import { PRODUCT_EVENT_TYPES } from '@/utils/rudderstack';
import { usePathname } from '@/navigation';
import SiblingsErrorModal from '../Siblings/SiblingsErrorModal';
import { ERROR_KINDS, ITEM_ORIGINS } from '@/core/constants';
import useLocale from '@/hooks/useLocale';
import { TypeSubscriptionRecord } from '@/lib/api/subscriptionManager/types';
import { useSubscriberShop } from '@/hooks/useSubscriberShop';
import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import { findApplicableErrorDetails } from '../Errors/utils';

import useSubscriptions from '@/lib/api/subscriptionManager/hooks/useSubscriptions';
import {
  filterInvalidSubscriptionTypes,
  isSubscriptionActive,
  isSubscriptionCancelled,
  isSubscriptionFinished,
} from '@/lib/api/subscriptionManager/helpers/subscriptionHelpers';

export interface SubscriberShopCollectionsProps
  extends TypeSubscriberShopCollectionsFields {
  data: TypeSubscriberShopPageData;
}

const SubscriberShopCollections: FC<SubscriberShopCollectionsProps> = ({
  data,
  translations,
  additionalCopy,
  reactivateSubscriptionModal,
  playKitsLimitModal,
  lifetimeMaxModal,
  siblingsErrorModal,
  filterBy,
  disableCollectionHeaderLabels,
}) => {
  const locale = useLocale();
  const pathname = usePathname();
  const { inventoryStatusCopy, userUuid, authToken } = data;
  const [numVisibleCards, setNumVisibleCards] = useState<{
    [key: string]: number;
  }>({});
  const [shouldBlockSubShopAccess, setShouldBlockSubShopAccess] =
    useState(true);
  const [
    targetedSubscriptionForReactivation,
    setTargetedSubscriptionForReactivation,
  ] = useState<TypeSubscriptionRecord | undefined>(undefined);
  const [isPlayKitsLimitModalOpen, setIsPlayKitsLimitModalOpen] =
    useState(false);
  const [isLifetimeMaxModalOpen, setIsLifetimeMaxModalOpen] = useState(false);
  const [addedProducts, setAddedProducts] = useState<
    TypeSubscriberShopProduct[]
  >([]);
  const [isSequential, setIsSequential] = useState(false);
  const [sequentialKits, setSequentialKits] = useState<
    TypeSubscriberShopProduct[]
  >([]);
  const qtyVal = 1;
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.DEFAULT,
  );

  const {
    data: subscriberShopData,
    isLoading: subscriberShopDataLoading,
    error: subscriberShopDataError,
  } = useSubscriberShop({
    userUuid,
    authToken,
    locale,
    filterBy,
  });

  const {
    data: subscriptions,
    loading: subscriptionsLoading,
    error: subscriptionsError,
  } = useSubscriptions({ userUuid });

  const { collections } = { ...subscriberShopData };

  useEffect(() => {
    if (subscriptions) {
      const validSubscriptions = filterInvalidSubscriptionTypes(subscriptions);
      const anyActiveSubscriptions =
        validSubscriptions.some(isSubscriptionActive);
      const anyAgedOutFullSubscriptions = validSubscriptions.some(
        (subscription) =>
          isSubscriptionFinished(subscription) &&
          !subscription.partial_subscription,
      );
      const canceledSubscriptions = validSubscriptions.filter(
        isSubscriptionCancelled,
      );

      if (canceledSubscriptions[0] !== targetedSubscriptionForReactivation) {
        setTargetedSubscriptionForReactivation(canceledSubscriptions[0]);
      }

      const shouldBlock = !(
        anyActiveSubscriptions || anyAgedOutFullSubscriptions
      );
      if (shouldBlock !== shouldBlockSubShopAccess) {
        setShouldBlockSubShopAccess(shouldBlock);
      }
    }
  }, [
    subscriptions,
    targetedSubscriptionForReactivation,
    shouldBlockSubShopAccess,
  ]);

  useEffect(() => {
    if (collections) {
      const initialNumVisibleCards = collections.reduce(
        (acc, collection) => {
          acc[collection.anchor] = DEFAULT_PRODUCT_CARDS_COUNT;
          return acc;
        },
        {} as { [key: string]: number },
      );
      if (
        JSON.stringify(initialNumVisibleCards) !==
        JSON.stringify(numVisibleCards)
      ) {
        setNumVisibleCards(initialNumVisibleCards);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections]);

  if (subscriberShopDataLoading || subscriptionsLoading)
    return (
      <div className="h-screen">
        <Loader loading className="mt-20 mb-40" />
      </div>
    );

  if (subscriberShopDataError || subscriptionsError) {
    return (
      <div className="mt-20">
        <Errors errors={errors} heading={heading} />
      </div>
    );
  }

  const handleAddedProducts = (addedProducts: TypeSubscriberShopProduct[]) => {
    setAddedProducts(addedProducts);

    const { sequential, kits } = findSequentialKitsOverSixMonths(addedProducts);
    setIsSequential(sequential);
    setSequentialKits(kits);

    if (sequential) {
      setIsPlayKitsLimitModalOpen(true);
    }
  };

  const updateVisibleCards = (anchor: string) => {
    setNumVisibleCards((prevState) => ({
      ...prevState,
      [anchor]: prevState[anchor] + DEFAULT_PRODUCT_CARDS_COUNT,
    }));
  };

  const handleRemoveSequentialKits = async () => {
    try {
      const variantIdsToRemove = sequentialKits.map(
        (kit) => kit.shopifyProduct.variants?.[0].id,
      );

      const response = await removeFromCart(variantIdsToRemove, {
        noredirect: 1,
      });

      if (response.ok) {
        mutate('/cart');
        sequentialKits.forEach(({ shopifyProduct, product }) => {
          const variant = shopifyProduct.variants?.[0];
          pushEvent('removeFromCart', {
            qtyVal,
            shopifyProduct,
            variant,
          });
          captureTypeProductEvent(PRODUCT_EVENT_TYPES.PRODUCT_REMOVED, {
            shopifyProduct,
            variant,
            product,
            quantity: qtyVal,
            url: pathname || '',
          });
        });
      } else {
        console.error(
          'Failed to remove products from cart:',
          (response as TypeResponseError).errorMessage,
        );
      }
    } catch (error) {
      console.error('Error removing products from cart:', error);
    }
  };

  const handleRemoveLastSequentialKit = async () => {
    try {
      const lastKit = addedProducts[addedProducts.length - 1];
      const variantIdToRemove = lastKit.shopifyProduct.variants[0].id;
      const { shopifyProduct, product } = lastKit;
      const variant = shopifyProduct.variants?.[0];

      setAddedProducts((prevState) => {
        const filteredProducts = prevState.filter(
          (addedProduct) => addedProduct.product.uuid !== lastKit.product.uuid,
        );
        return filteredProducts;
      });

      const response = await removeFromCart([variantIdToRemove], {
        noredirect: 1,
      });

      if (response.ok) {
        mutate('/cart');
        pushEvent('removeFromCart', {
          qtyVal,
          shopifyProduct,
          variant,
        });
        captureTypeProductEvent(PRODUCT_EVENT_TYPES.PRODUCT_REMOVED, {
          shopifyProduct,
          variant,
          product,
          quantity: qtyVal,
          url: pathname || '',
        });
      }
    } catch (error) {
      console.error('Error removing last sequential kit from cart:', error);
    }
  };

  const t = generateTranslationsObject(
    translations.fields,
  ) as TypeSubscriberShopPageTranslations;

  const hyphenatedToCamelCase = (str: string): string => {
    return str
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]+(.)/g, (_m, chr) => chr.toUpperCase());
  };

  return (
    <>
      <section
        data-testid="sub-shop-collections"
        className="mx-auto max-w-[1128px] p-6 lg:py-12"
      >
        {collections?.map(
          ({ anchor, name, description, products }, collectionIndex) => {
            const validCollectionIndex =
              collectionIndex % Object.keys(DIVIDER_IMAGES).length;
            const nameKey = hyphenatedToCamelCase(
              `collection-${anchor}-name`,
            ) as keyof TypeSubscriberShopPageTranslations;
            const descriptionKey = hyphenatedToCamelCase(
              `collection-${anchor}-description`,
            ) as keyof TypeSubscriberShopPageTranslations;

            return (
              <div
                data-testid={`collection_${anchor}`}
                key={anchor}
                className="mb-20 last:mb-8"
              >
                {collectionIndex !== 0 && (
                  <Image
                    src={DIVIDER_IMAGES[validCollectionIndex]}
                    width={121}
                    height={13}
                    alt="divider"
                    className="mb-6 h-[5px] w-[71px] md:h-[13px] md:w-[121px]"
                  />
                )}
                {!disableCollectionHeaderLabels && (
                  <>
                    <HeaderText
                      classes="mb-4"
                      size={HeaderSize.H2}
                      text={t[nameKey] || name}
                    />
                    <p className="mb-4 text-xl leading-8">
                      {t[descriptionKey] || description}
                    </p>
                  </>
                )}
                <div className="grid grid-cols-1 gap-2 md:gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {products
                    .slice(0, numVisibleCards[anchor])
                    .map((subscriberShopProduct) => {
                      return (
                        <SiblingsProductCard
                          key={`${collectionIndex}-${subscriberShopProduct.product.uuid}`}
                          subscriberShopProduct={subscriberShopProduct}
                          addedProducts={addedProducts}
                          handleAddedProducts={handleAddedProducts}
                          openLifetimeMaxModal={() =>
                            setIsLifetimeMaxModalOpen(true)
                          }
                          translations={t}
                          inventoryStatusCopy={inventoryStatusCopy}
                          openErrorModal={() => setIsErrorModalOpen(true)}
                          itemOrigin={ITEM_ORIGINS.SUB_SHOP}
                        />
                      );
                    })}
                </div>
                {numVisibleCards[anchor] < products.length && (
                  <div className="mt-10 text-center">
                    <button
                      onClick={() => updateVisibleCards(anchor)}
                      className="font-lg text-indigo underline"
                    >
                      {t.seeMoreCta}
                    </button>
                  </div>
                )}
              </div>
            );
          },
        )}
        {additionalCopy && (
          <div className="mt-5 text-center">
            <RichText
              openLinkSameWindow={true}
              content={additionalCopy as Document}
            />
          </div>
        )}
      </section>

      <ReactivateSubscriptionModal
        isOpen={shouldBlockSubShopAccess}
        handleClose={() => setShouldBlockSubShopAccess(false)}
        targetedSubscriptionForReactivation={
          targetedSubscriptionForReactivation
        }
        translations={t}
        {...(reactivateSubscriptionModal.fields as TypeModalFields)}
      />

      <PlayKitsLimitModal
        isOpen={isPlayKitsLimitModalOpen}
        handleClose={async () => {
          if (isSequential) {
            await handleRemoveLastSequentialKit();
          }
          setIsPlayKitsLimitModalOpen(false);
        }}
        handleRemoveSequentialKits={async () => {
          if (isSequential) {
            await handleRemoveSequentialKits();
          }
        }}
        translations={t}
        {...(playKitsLimitModal.fields as TypeModalFields)}
      />

      <LifetimeMaxModal
        isOpen={isLifetimeMaxModalOpen}
        handleClose={() => setIsLifetimeMaxModalOpen(false)}
        translations={t}
        {...(lifetimeMaxModal.fields as TypeModalFields)}
      />

      <SiblingsErrorModal
        isOpen={isErrorModalOpen}
        handleClose={() => setIsErrorModalOpen(false)}
        translations={t}
        {...(siblingsErrorModal.fields as TypeModalFields)}
      />
    </>
  );
};

export default React.memo(SubscriberShopCollections);
